import {
  MAX_CAPTURES,
  MAX_CAPTURE_BASE64_CHARS,
  type ToolsSharedCaptureEntry,
} from '@/components/tools-shared-case-data';
import { stripWebpToRawBase64 } from '@/shared/userImageStorage';

export type CaptureFavoriteSink = {
  hydrated: boolean;
  setCaptures: (
    action:
      | ToolsSharedCaptureEntry[]
      | ((prev: ToolsSharedCaptureEntry[]) => ToolsSharedCaptureEntry[]),
  ) => void;
};

/**
 * The Favorites row for an uploaded still.
 *
 * A moderated capture has to arrive with both the type that marks it flagged
 * and the obfuscated preview to paint in its place — an entry carrying only the
 * s3Uri renders the original. Every save path builds its entry here so none of
 * them can quietly drop those fields.
 */
export function buildCaptureFavoriteEntry(args: {
  s3Uri: string;
  contentModerationType?: string;
  /** Data URL or raw base64; normalised and dropped when it exceeds the row budget. */
  obfuscatedCaptureData?: string;
  imageDescription?: string;
}): ToolsSharedCaptureEntry {
  const obfRaw = args.obfuscatedCaptureData
    ? stripWebpToRawBase64(args.obfuscatedCaptureData)
    : undefined;
  const keepObf = !!obfRaw && obfRaw.length > 0 && obfRaw.length <= MAX_CAPTURE_BASE64_CHARS;
  return {
    id: crypto.randomUUID(),
    s3Uri: args.s3Uri,
    createdAt: Date.now(),
    ...(args.contentModerationType
      ? { contentModerationType: args.contentModerationType }
      : {}),
    ...(keepObf ? { obfuscatedWebpBase64: obfRaw } : {}),
    ...(args.imageDescription ? { imageDescription: args.imageDescription } : {}),
  };
}

function prependFavorites(
  prev: ToolsSharedCaptureEntry[],
  entries: readonly ToolsSharedCaptureEntry[],
): ToolsSharedCaptureEntry[] {
  if (entries.length === 0) return prev;
  return [...entries].reverse().concat(prev).slice(0, MAX_CAPTURES);
}

export function appendCaptureFavorite(
  sink: CaptureFavoriteSink,
  entry: ToolsSharedCaptureEntry,
): void {
  sink.setCaptures((prev) => prependFavorites(prev, [entry]));
}

/**
 * Drain queued captures into Favorites once the shared case bundle is hydrated.
 * Oldest queued entry is written first so the newest stays at the front.
 */
export function flushQueuedCaptureFavorites(
  sink: CaptureFavoriteSink | null | undefined,
  pending: readonly ToolsSharedCaptureEntry[],
): ToolsSharedCaptureEntry[] {
  if (pending.length === 0) return [];
  if (!sink?.hydrated) return [...pending];
  sink.setCaptures((prev) => prependFavorites(prev, pending));
  return [];
}

/**
 * Queue ``entry`` then flush against ``sink``. Pass the *live* sink after any
 * ``await`` — a snapshot taken before upload can still look unhydrated after
 * the bundle has already hydrated, which would leave the capture stuck in a
 * ref that nothing flushes (TR-2874).
 */
export function persistCaptureFavorite(
  sink: CaptureFavoriteSink | null | undefined,
  entry: ToolsSharedCaptureEntry,
  pending: readonly ToolsSharedCaptureEntry[] = [],
): ToolsSharedCaptureEntry[] {
  return flushQueuedCaptureFavorites(
    sink,
    [...pending, entry].slice(-MAX_CAPTURES),
  );
}
